import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Database, Sigma, X } from 'lucide-react';
import {
  ChartData,
  KPIMetric,
  LineageV2,
  LineageV2Derivation,
  LineageV2Dimension,
  LineageV2InputColumn,
  LineageV2OdataRef,
  LineageV2Value,
} from '../../types/dashboard';
import { formatValue } from '../../utils/numberFormat';

// Compact, focused lineage drawer driven by the backend's
// ``lineage_v2`` payload. Three sections in the same right-rail shell
// the previous panel used:
//
//   1. WHAT THIS IS         — one-sentence plain-English description.
//   2. VALUES               — every value in the KPI / chart with its
//                             column, derivation (if any), and a link
//                             to the OData call that produced it.
//   3. ODATA REFERENCES     — collapsible per-call detail (URL,
//                             SELECT, FILTER, GROUP BY, ORDER BY,
//                             TOP, params).
//
// Replaces the older 785-line panel that walked across calculationInfo,
// drillDown.operationPlan, per-comparator lineage blocks, and a
// firstRenderable() fallback chain. Reads ONLY ``metric.lineage_v2`` /
// ``chart.lineage_v2``. Empty-state when absent (legacy responses).

export type LineageContext =
  | { kind: 'kpi'; metric: KPIMetric }
  | { kind: 'chart'; chart: ChartData };

interface LineagePanelProps {
  context: LineageContext | null;
  onClose: () => void;
}

const MIN_W = 380;
const MAX_W = 920;
const DEFAULT_W = 620;

// ─────────────────────────────────────────────────────────────────────
// Section primitives — small, repetition-friendly.
// ─────────────────────────────────────────────────────────────────────

export function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7B6B72]">
      {children}
    </div>
  );
}

export function LineageCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-[12px] border border-[#EDE6E1] bg-white p-4 ${className}`}>
      {children}
    </div>
  );
}

function CodeChip({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <code
      title={title}
      className="break-all rounded-[6px] border border-[#E7DAD3] bg-[#FAF7F1] px-1.5 py-[2px] font-mono text-[11.5px] leading-snug text-[#3F3338]"
    >
      {children}
    </code>
  );
}

function SourceBadge({ source }: { source: 'tool' | 'derived' }) {
  const tone =
    source === 'derived'
      ? 'border-[#C9A24E]/40 bg-[#FBF3DC] text-[#7A5A12]'
      : 'border-[#E7DAD3] bg-[#FAF7F1] text-[#6E3450]';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] font-display text-[10px] font-semibold uppercase tracking-[0.1em] ${tone}`}
    >
      {source === 'derived' ? <Sigma className="h-2.5 w-2.5" /> : <Database className="h-2.5 w-2.5" />}
      {source}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// VALUES section — per-row expand reveals derivation + odata_ref link.
// ─────────────────────────────────────────────────────────────────────

function lineageDisplayValue(value: LineageV2Value, valueUnit?: string): string {
  const raw = value.value_raw;
  if (raw === null || raw === undefined || raw === '') return '';
  return formatValue(raw, { unit: valueUnit });
}

function ValueRow({
  value,
  valueUnit,
  expanded,
  onToggle,
  onScrollToRef,
}: {
  value: LineageV2Value;
  valueUnit?: string;
  expanded: boolean;
  onToggle: () => void;
  onScrollToRef: (id: string) => void;
}) {
  const hasDetail = !!value.derivation || !!value.odata_ref;
  const displayValue = lineageDisplayValue(value, valueUnit);
  return (
    <div className="border-t border-[#EDE6E1] first:border-t-0">
      <button
        type="button"
        onClick={hasDetail ? onToggle : undefined}
        className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors ${
          hasDetail ? 'cursor-pointer hover:bg-[#FAF7F1]/60' : 'cursor-default'
        }`}
        aria-expanded={expanded}
      >
        <div className="mt-[2px] flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#7B6B72]">
          {hasDetail ? (
            expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
          ) : (
            <span className="inline-block h-1 w-1 rounded-full bg-[#D5C2CB]" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-display text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">
              {value.label}
            </div>
            {displayValue && (
              <div className="shrink-0 font-display text-[14px] font-semibold tabular-nums text-[#1F1A20]">
                {displayValue}
              </div>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <CodeChip title={value.column}>{value.column}</CodeChip>
            <SourceBadge source={value.source} />
            <span className="text-[11.5px] text-[#7B6B72]">{value.column_label}</span>
          </div>
        </div>
      </button>
      {expanded && hasDetail && (
        <div className="space-y-3 bg-[#FAF7F1]/50 px-6 pb-3 pt-2 text-[12px]">
          {value.derivation && (
            <DerivationBlock derivation={value.derivation} onScrollToRef={onScrollToRef} />
          )}
          {value.odata_ref && (
            <button
              type="button"
              onClick={() => onScrollToRef(value.odata_ref!)}
              className="inline-flex items-center gap-1.5 rounded-[6px] border border-[#E7DAD3] bg-white px-2 py-1 text-[11px] text-[#6E3450] transition-colors hover:bg-[#f5e9ee]"
            >
              <Database className="h-3 w-3" />
              See OData call <CodeChip>{value.odata_ref}</CodeChip>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Derivation block + recursive InputRow.
//
// Displays the formula (pretty + raw), then a list of input columns
// underneath. Each input is itself a row: chevron, column code chip,
// source badge, and (when expanded) its own column source / OData
// link / sub-derivation. Recursion bottoms out either at a tool-source
// column or when the backend marks the derivation as truncated.
// ─────────────────────────────────────────────────────────────────────

function DerivationBlock({
  derivation,
  onScrollToRef,
}: {
  derivation: LineageV2Derivation;
  onScrollToRef: (id: string) => void;
}) {
  return (
    <div>
      <div className="mb-1 font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">
        Derivation
      </div>
      <div className="rounded-[8px] border border-[#EDE6E1] bg-white px-2.5 py-2">
        {derivation.formula_pretty &&
        derivation.formula_pretty !== derivation.formula ? (
          <div className="text-[12.5px] text-[#1F1A20]">{derivation.formula_pretty}</div>
        ) : null}
        {derivation.formula && (
          <div className="mt-1 font-mono text-[11.5px] text-[#3F3338]">{derivation.formula}</div>
        )}
        {derivation.truncated && (
          <div className="mt-1 text-[11px] italic text-[#9B607A]">
            Further inputs available — open the source columns at this depth to view them.
          </div>
        )}
        {derivation.inputs.length > 0 && (
          <div className="mt-2">
            <div className="mb-1 text-[10px] uppercase tracking-[0.12em] text-[#7B6B72]">
              Inputs
            </div>
            <div className="rounded-[6px] border border-[#EDE6E1] bg-[#FAF7F1]/50">
              {derivation.inputs.map((rawInput, idx) => {
                // Defensively normalise legacy / partial payloads.
                // Old responses (pre-recursive) shipped ``inputs`` as
                // a list of column-name strings; the current backend
                // ships full ``LineageV2InputColumn`` objects. Render
                // either gracefully so a stale persisted message
                // doesn't blank the Inputs section.
                const input: LineageV2InputColumn =
                  typeof rawInput === 'string'
                    ? {
                        column: rawInput,
                        column_label: rawInput,
                        source: 'tool',
                        derivation: null,
                        odata_ref: null,
                      }
                    : (rawInput as LineageV2InputColumn);
                return (
                  <InputRow
                    key={`${input.column || 'input'}-${idx}`}
                    input={input}
                    onScrollToRef={onScrollToRef}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InputRow({
  input,
  onScrollToRef,
}: {
  input: LineageV2InputColumn;
  onScrollToRef: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // Defensive: when a legacy payload sends a string (or a record with
  // a missing source field), the previous component crashed silently
  // — empty chip / undefined badge tone. Coerce to safe defaults so
  // the Inputs section always renders something readable.
  const column = input.column || '(unknown)';
  const columnLabel = input.column_label || column;
  const source: 'tool' | 'derived' = input.source === 'derived' ? 'derived' : 'tool';
  const hasDetail = !!input.derivation || !!input.odata_ref;
  // Mirrors ValueRow + DimensionRow chrome: chevron · top label
  // (column display name) · bottom chip + source badge. Expand
  // reveals the derivation block (if derived) and / or the OData
  // ref jump pill.
  return (
    <div className="border-t border-[#EDE6E1] first:border-t-0">
      <button
        type="button"
        onClick={hasDetail ? () => setExpanded((v) => !v) : undefined}
        className={`flex w-full items-start gap-2 px-2.5 py-2 text-left transition-colors ${
          hasDetail ? 'cursor-pointer hover:bg-white' : 'cursor-default'
        }`}
        aria-expanded={expanded}
      >
        <div className="mt-[2px] flex h-3 w-3 shrink-0 items-center justify-center text-[#7B6B72]">
          {hasDetail ? (
            expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
          ) : (
            <span className="inline-block h-1 w-1 rounded-full bg-[#D5C2CB]" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">
            {columnLabel}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <CodeChip title={column}>{column}</CodeChip>
            <SourceBadge source={source} />
          </div>
        </div>
      </button>
      {expanded && hasDetail && (
        <div className="space-y-2 bg-white px-5 pb-2 pt-1 text-[12px]">
          {input.derivation && (
            <DerivationBlock derivation={input.derivation} onScrollToRef={onScrollToRef} />
          )}
          {input.odata_ref && (
            <button
              type="button"
              onClick={() => onScrollToRef(input.odata_ref!)}
              className="inline-flex items-center gap-1.5 rounded-[6px] border border-[#E7DAD3] bg-[#FAF7F1] px-2 py-1 text-[11px] text-[#6E3450] transition-colors hover:bg-[#f5e9ee]"
            >
              <Database className="h-3 w-3" />
              See OData call <CodeChip>{input.odata_ref}</CodeChip>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DimensionRow({
  dim,
  expanded,
  onToggle,
  onScrollToRef,
}: {
  dim: LineageV2Dimension;
  expanded: boolean;
  onToggle: () => void;
  onScrollToRef: (id: string) => void;
}) {
  // Mirrors ValueRow: chevron · faded uppercase label on top, column
  // chip + source badge on the bottom, expand-to-reveal OData ref.
  // Dimensions don't carry a numeric value so the top-right slot
  // (where ValueRow shows "$12.6M") stays empty — same hierarchy,
  // same chrome, just one less data point.
  const hasDetail = !!dim.odata_ref;
  return (
    <div className="border-t border-[#EDE6E1] first:border-t-0">
      <button
        type="button"
        onClick={hasDetail ? onToggle : undefined}
        className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors ${
          hasDetail ? 'cursor-pointer hover:bg-[#FAF7F1]/60' : 'cursor-default'
        }`}
        aria-expanded={expanded}
      >
        <div className="mt-[2px] flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#7B6B72]">
          {hasDetail ? (
            expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
          ) : (
            <span className="inline-block h-1 w-1 rounded-full bg-[#D5C2CB]" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">
            {dim.label}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <CodeChip title={dim.column}>{dim.column}</CodeChip>
            <SourceBadge source="tool" />
          </div>
        </div>
      </button>
      {expanded && hasDetail && dim.odata_ref && (
        <div className="bg-[#FAF7F1]/50 px-6 pb-3 pt-2 text-[12px]">
          <button
            type="button"
            onClick={() => onScrollToRef(dim.odata_ref!)}
            className="inline-flex items-center gap-1.5 rounded-[6px] border border-[#E7DAD3] bg-white px-2 py-1 text-[11px] text-[#6E3450] transition-colors hover:bg-[#f5e9ee]"
          >
            <Database className="h-3 w-3" />
            See OData call <CodeChip>{dim.odata_ref}</CodeChip>
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ODATA REFERENCES section — one collapsible card per call.
// ─────────────────────────────────────────────────────────────────────

function OdataRefCard({
  ref_,
  expanded,
  onToggle,
}: {
  ref_: LineageV2OdataRef;
  expanded: boolean;
  onToggle: () => void;
}) {
  const params = Object.entries(ref_.params || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );
  const resolvedFilters = (ref_.resolved_filters || []).filter(
    (filter) => filter && filter.value !== null && filter.value !== undefined && filter.value !== '',
  );
  return (
    <div id={`odata-ref-${ref_.id}`} className="rounded-[10px] border border-[#EDE6E1] bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-[#FAF7F1]/60"
        aria-expanded={expanded}
      >
        <Database className="h-3.5 w-3.5 shrink-0 text-[#6E3450]" />
        <CodeChip>{ref_.id}</CodeChip>
        <span className="min-w-0 flex-1 truncate text-[11.5px] text-[#7B6B72]">{ref_.model_name || ref_.url}</span>
        {expanded ? <ChevronDown className="h-3 w-3 text-[#7B6B72]" /> : <ChevronRight className="h-3 w-3 text-[#7B6B72]" />}
      </button>
      {expanded && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 border-t border-[#EDE6E1] bg-[#FAF7F1]/50 px-3 py-3 text-[12px]">
          <Field label="URL"><span className="break-all font-mono text-[11.5px] text-[#3F3338]">{ref_.url || '—'}</span></Field>
          {ref_.select.length > 0 && (
            <Field label="$select">
              <div className="flex flex-wrap gap-1.5">
                {ref_.select.map((col, idx) => <CodeChip key={`${ref_.id}-select-${idx}`}>{col}</CodeChip>)}
              </div>
            </Field>
          )}
          {resolvedFilters.length > 0 && (
            <Field label="Resolved filters">
              <div className="space-y-1.5">
                {resolvedFilters.map((filter, idx) => (
                  <div key={`${ref_.id}-resolved-filter-${idx}`} className="flex flex-wrap items-center gap-1.5">
                    <CodeChip title={filter.column}>{filter.label || filter.column}</CodeChip>
                    <span className="text-[11.5px] text-[#7B6B72]">=</span>
                    <span className="rounded-[6px] border border-[#E7DAD3] bg-white px-1.5 py-[2px] text-[11.5px] text-[#3F3338]">
                      {String(filter.value)}
                    </span>
                    {filter.raw && String(filter.raw) !== String(filter.value) && (
                      <span className="text-[11px] text-[#7B6B72]">from "{String(filter.raw)}"</span>
                    )}
                  </div>
                ))}
              </div>
            </Field>
          )}
          {ref_.filter && (
            <Field label="$filter"><span className="break-all font-mono text-[11.5px] text-[#3F3338]">{ref_.filter}</span></Field>
          )}
          {ref_.group_by.length > 0 && (
            <Field label="Group by">
              <div className="flex flex-wrap gap-1.5">
                {ref_.group_by.map((col, idx) => <CodeChip key={`${ref_.id}-groupby-${idx}`}>{col}</CodeChip>)}
              </div>
            </Field>
          )}
          {ref_.order_by && (
            <Field label="$orderby"><span className="font-mono text-[11.5px] text-[#3F3338]">{ref_.order_by}</span></Field>
          )}
          {ref_.top !== null && ref_.top !== undefined && (
            <Field label="$top"><span className="font-mono text-[11.5px] text-[#3F3338]">{ref_.top}</span></Field>
          )}
          {params.length > 0 && (
            <Field label="Params">
              <div className="space-y-1">
                {params.map(([k, v]) => (
                  <div key={k} className="flex flex-wrap items-center gap-1.5">
                    <CodeChip>{k}</CodeChip>
                    <span className="text-[11.5px] text-[#3F3338]">{String(v)}</span>
                  </div>
                ))}
              </div>
            </Field>
          )}
          {ref_.row_count !== null && ref_.row_count !== undefined && (
            <Field label="Rows"><span className="font-mono text-[11.5px] text-[#3F3338]">{ref_.row_count.toLocaleString()}</span></Field>
          )}
        </dl>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="pt-[1px] font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">
        {label}
      </dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// LineageBody — shared between KPI / Chart contexts.
// ─────────────────────────────────────────────────────────────────────

/**
 * Reusable lineage body — renders the WHAT THIS IS / Dimensions /
 * Values / OData References sections. Exported so the
 * ChartExpandedModal can reuse the same chrome inside its inspector
 * sidebar (single source of truth for "lineage as a panel").
 *
 * The optional ``extraSection`` slot renders below VALUES and above
 * OData References — used by the modal to inject a per-series
 * ``Data`` stats block (sum / avg / min / max from the visible
 * rows). The Info-icon side panel doesn't pass it.
 */
export function LineageBody({
  title,
  subtitle,
  lineage,
  valueUnit,
  extraSection,
}: {
  title: string;
  subtitle?: string;
  lineage: LineageV2 | undefined;
  valueUnit?: string;
  extraSection?: React.ReactNode;
}) {
  // Hooks must run unconditionally — keep them at the top.
  const [expandedValues, setExpandedValues] = useState<Set<string>>(new Set());
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(new Set());
  const [expandedRefs, setExpandedRefs] = useState<Set<string>>(new Set());

  if (!lineage) {
    return (
      <div className="space-y-4">
        <div>
          <div className="font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9B607A]">
            Data Lineage
          </div>
          <h2 className="mt-1 font-display text-[20px] font-semibold leading-[1.2] text-[#1F1A20]">{title}</h2>
          {subtitle && <p className="mt-1 text-[12.5px] text-[#7B6B72]">{subtitle}</p>}
        </div>
        <LineageCard>
          <div className="text-[12.5px] text-[#7B6B72]">
            Lineage details aren't available for this item. This usually
            means the response is from a legacy run that predates the
            unified lineage payload, or the column wasn't tagged with
            its source tool call.
          </div>
        </LineageCard>
      </div>
    );
  }

  const toggleValue = (slot: string) =>
    setExpandedValues((prev) => {
      const next = new Set(prev);
      if (next.has(slot)) {
        next.delete(slot);
      } else {
        next.add(slot);
      }
      return next;
    });

  const toggleDimension = (key: string) =>
    setExpandedDimensions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const scrollToRef = (id: string) => {
    setExpandedRefs((prev) => new Set(prev).add(id));
    requestAnimationFrame(() => {
      const el = document.getElementById(`odata-ref-${id}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const toggleRef = (id: string) =>
    setExpandedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="space-y-5">
      {/* Title block */}
      <div>
        <div className="font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9B607A]">
          Data Lineage
        </div>
        <h2 className="mt-1 font-display text-[20px] font-semibold leading-[1.2] text-[#1F1A20]">{title}</h2>
        {subtitle && <p className="mt-1 text-[12.5px] text-[#7B6B72]">{subtitle}</p>}
      </div>

      {/* "What this is" section removed — the title block above
          already conveys the metric/chart name. ``lineage.what_it_is``
          is still on the payload for downstream consumers (chat, PPT
          export) but no longer rendered here. */}

      {/* Dimensions (charts only) */}
      {lineage.dimensions && lineage.dimensions.length > 0 && (
        <section className="space-y-2">
          <SectionHeader>Dimensions</SectionHeader>
          <LineageCard className="p-0">
            {lineage.dimensions.map((d) => {
              const key = `${d.slot}-${d.column}`;
              return (
                <DimensionRow
                  key={key}
                  dim={d}
                  expanded={expandedDimensions.has(key)}
                  onToggle={() => toggleDimension(key)}
                  onScrollToRef={scrollToRef}
                />
              );
            })}
          </LineageCard>
        </section>
      )}

      {/* Values */}
      {lineage.values.length > 0 && (
        <section className="space-y-2">
          <SectionHeader>
            {lineage.dimensions ? `Values (${lineage.values.length})` : `Values (${lineage.values.length})`}
          </SectionHeader>
          <LineageCard className="p-0">
            {lineage.values.map((v) => (
              <ValueRow
                key={v.slot}
                value={v}
                valueUnit={valueUnit}
                expanded={expandedValues.has(v.slot)}
                onToggle={() => toggleValue(v.slot)}
                onScrollToRef={scrollToRef}
              />
            ))}
          </LineageCard>
        </section>
      )}

      {/* Modal-only Data stats slot (Series sum / avg / min / max).
          The Info-icon side panel doesn't pass ``extraSection`` so
          it's a no-op there — keeps the two surfaces in lockstep
          except for the one thing only the modal can compute. */}
      {extraSection}

      {/* OData references */}
      {lineage.odata_refs.length > 0 && (
        <section className="space-y-2">
          <SectionHeader>OData References ({lineage.odata_refs.length})</SectionHeader>
          <div className="space-y-2">
            {lineage.odata_refs.map((r) => (
              <OdataRefCard
                key={r.id}
                ref_={r}
                expanded={expandedRefs.has(r.id)}
                onToggle={() => toggleRef(r.id)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Top-level shell — same right-rail drawer + drag-resize as before.
// ─────────────────────────────────────────────────────────────────────

export function LineagePanel({ context, onClose }: LineagePanelProps) {
  const [width, setWidth] = useState<number>(DEFAULT_W);
  const dragRef = React.useRef<{ startX: number; startW: number } | null>(null);

  React.useEffect(() => {
    if (!context) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [context, onClose]);

  const startResize = (e: React.MouseEvent) => {
    dragRef.current = { startX: e.clientX, startW: width };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = dragRef.current.startX - ev.clientX;
      const next = Math.min(MAX_W, Math.max(MIN_W, dragRef.current.startW + dx));
      setWidth(next);
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (!context) return null;

  const { title, subtitle, lineage, valueUnit } =
    context.kind === 'kpi'
      ? {
          title: context.metric.label,
          subtitle: context.metric.lineage_v2?.what_it_is ? undefined : context.metric.unit,
          lineage: context.metric.lineage_v2,
          valueUnit: context.metric.unit,
        }
      : {
          title: context.chart.title,
          subtitle: context.chart.lineage_v2?.what_it_is ? undefined : context.chart.caption,
          lineage: context.chart.lineage_v2,
          valueUnit: undefined,
        };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/15 backdrop-blur-[1px]" onClick={onClose} />
      <aside
        className="absolute top-0 right-0 bottom-0 flex flex-col border-l border-[#EDE6E1] bg-white shadow-2xl"
        style={{ width: `${width}px` }}
      >
        <div
          onMouseDown={startResize}
          className="absolute top-0 left-0 h-full w-[6px] cursor-col-resize hover:bg-[#6E3450]/15"
          title="Drag to resize"
        />
        <header className="absolute right-5 top-5 z-20">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-[#7B6B72] transition-colors hover:bg-[#FAF7F1] hover:text-[#6E3450]"
            aria-label="Close lineage panel"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-7 pb-8 pt-6">
          <LineageBody title={title || ''} subtitle={subtitle || ''} lineage={lineage} valueUnit={valueUnit} />
        </div>
      </aside>
    </div>
  );
}
